import { Pagination, PaginationResponse, Permission, Role } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit, Group } from '@mui/icons-material';
import { Button, Chip, Drawer, Stack, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminTable, AdminTableColumn, Highlight } from 'src/Common/Components/AdminTable';
import { RowActionsMenu } from 'src/Common/Components/RowActionsMenu';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import useRequest from 'src/Common/Hooks/useRequest';
import Permissions from 'src/Common/Permissions';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { SettingsSection } from 'src/UserManagement/Components/Settings/SettingsSection';
import { RolesAdminPanel } from './RolesAdminPanel';

const DRAWER_WIDTH = 560;

export const RolesAdminSection = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const navigate = useNavigate();
  const { refresh: triggerRefresh } = usePagination();

  const [, startSaving] = useRequest<void>();
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [, startFetchingPerms] = useRequest<Permission[]>();

  const [drawerRole, setDrawerRole] = useState<Role | undefined>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [deleteRole, setDeleteRole] = useState<Role | undefined>();

  const canWrite = useMemo(() => hasPermission(Permissions.ROLES_WRITE), [hasPermission]);
  const canUsers = useMemo(() => hasPermission(Permissions.USERS_READ), [hasPermission]);

  useEffect(() => {
    startFetchingPerms(
      () => api.getPermissions(),
      (result) => setAvailablePermissions(result ?? []),
    );
  }, [api, startFetchingPerms]);

  const fetchItems = useCallback(
    (pagination: Pagination): Promise<PaginationResponse<Role>> => api.getRoles(pagination),
    [api],
  );

  const openCreate = useCallback(() => {
    setDrawerRole(undefined);
    setIsDrawerOpen(true);
  }, []);

  const openEdit = useCallback((role: Role) => {
    setDrawerRole(role);
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setDrawerRole(undefined);
  }, []);

  const saveHandler = useCallback(
    (payload: Role | Partial<Role>) => {
      startSaving(
        async () => {
          if (payload?.id === undefined && drawerRole === undefined) {
            await api.addRole(payload as Role);
          } else {
            await api.updateRole(payload as Role);
          }
        },
        () => {
          enqueueSnackbar(drawerRole ? 'Role updated.' : 'Role created.');
          closeDrawer();
          triggerRefresh();
        },
      );
    },
    [api, drawerRole, startSaving, closeDrawer, triggerRefresh],
  );

  const onDeleteConfirm = useCallback(() => {
    if (deleteRole === undefined) {
      return;
    }
    startSaving(
      () => api.deleteRole(deleteRole),
      () => {
        setDeleteRole(undefined);
        triggerRefresh();
      },
    );
  }, [api, deleteRole, startSaving, triggerRefresh]);

  const columns = useMemo<AdminTableColumn<Role>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        size: 220,
        renderCell: (row) => (
          <Typography fontWeight={600} noWrap>
            <Highlight text={row.name} field={'name'} />
          </Typography>
        ),
      },
      {
        field: 'coordinate',
        headerName: 'Coordinate',
        size: 300,
        renderCell: (row) => (
          <Typography variant={'body2'} color={'text.secondary'} noWrap>
            <Highlight text={row.coordinate} field={'coordinate'} />
          </Typography>
        ),
      },
      {
        field: 'permissions',
        headerName: 'Permissions',
        size: 320,
        sortable: false,
        filterable: false,
        renderCell: (row) => {
          const perms = row.permissions ?? [];
          if (perms.length === 0) {
            return (
              <Typography variant={'caption'} color={'text.disabled'}>
                none
              </Typography>
            );
          }
          const preview = perms.slice(0, 2);
          const remaining = perms.length - preview.length;
          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={0.25} sx={{ p: 1 }}>
                  {[...perms].sort().map((p) => (
                    <Typography key={p} variant={'caption'} fontFamily={'monospace'}>
                      {p}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Stack direction={'row'} spacing={0.5} alignItems={'center'} flexWrap={'wrap'}>
                {preview.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    size={'small'}
                    variant={'outlined'}
                    sx={{ height: 22, fontFamily: 'monospace', fontSize: 11 }}
                  />
                ))}
                {remaining > 0 && (
                  <Chip
                    label={`+${remaining}`}
                    size={'small'}
                    sx={{ height: 22, fontSize: 11, backgroundColor: 'action.selected', color: 'text.secondary' }}
                  />
                )}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: '__actions',
        headerName: '',
        size: 60,
        sortable: false,
        filterable: false,
        resizable: false,
        hideInColumnsPopover: true,
        align: 'center',
        renderCell: (row) => (
          <RowActionsMenu<Role>
            row={row}
            actions={[
              {
                label: 'Users',
                icon: <Group fontSize="small" />,
                onClick: (r) => navigate('/me/admin/roles/' + r.id + '/users'),
                disabled: () => !canUsers,
              },
              {
                label: 'Edit',
                icon: <Edit fontSize="small" />,
                onClick: openEdit,
                disabled: () => !canWrite,
              },
              {
                label: 'Delete',
                icon: <Delete fontSize="small" />,
                onClick: (r) => setDeleteRole(r),
                disabled: () => !canWrite,
              },
            ]}
          />
        ),
      },
    ],
    [canUsers, canWrite, navigate, openEdit],
  );

  return (
    <SettingsSection title={'Roles'} description={'Define roles and their permission sets.'}>
      <AdminTable<Role>
        fetchItems={fetchItems}
        columns={columns}
        initialSort={{ field: 'name', sort: 'asc' }}
        sortFieldMap={{
          name: 'R.name',
          coordinate: 'Node.materializedPath',
        }}
        primaryAction={
          canWrite ? (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add role
            </Button>
          ) : undefined
        }
      />

      <Drawer
        anchor={'right'}
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: DRAWER_WIDTH }, maxWidth: '100vw' } }}
      >
        <RolesAdminPanel
          role={drawerRole}
          availablePermissions={availablePermissions}
          closeHandler={closeDrawer}
          saveHandler={saveHandler}
        />
      </Drawer>

      <GenericDialog
        type={'confirmation'}
        maxWidth={'sm'}
        fullWidth
        centered
        open={!!deleteRole}
        title={'Delete Role'}
        text={deleteRole ? `Delete Role ${deleteRole.name}?` : ''}
        onClose={() => setDeleteRole(undefined)}
        onConfirm={onDeleteConfirm}
      />
    </SettingsSection>
  );
};
